/**
 * LessonPreview
 *
 * Right-side preview pane for the curriculum builder. Shows a quick
 * read-only sense of what the selected lesson contains — title, type,
 * description, and a type-appropriate content sample.
 *
 * Click "Edit lesson" to open the full editor modal. For more elaborate
 * lesson editing inline here, see the follow-up note in CourseCurriculumTab.
 */

// Registers the <mux-player> web component used by VideoViewer for Mux videos.
import '@mux/mux-player';
import { LessonType } from '../../apis/api-client';
import { Box, Button, Card, Chip, Typography } from '../ui';
import { EditIcon, FileTextIcon, LockIcon } from '../icons';
import { lessonTypeMeta } from './contentTypeMeta';
import { TiptapViewer } from '../common';
import VideoViewer from '../courseDetail/viewers/VideoViewer';
import { useVideoPlayback } from '../../hooks/useVideoPlayback';

interface LessonPreviewProps {
  lesson: any | null;
  onEdit?: (lesson: any) => void;
}


export default function LessonPreview({ lesson, onEdit }: LessonPreviewProps) {
  // Fetch the Mux signed playback token whenever a Video lesson is in the
  // preview. The hook no-ops for non-video lessons + Wistia (which the
  // VideoViewer renders via its own embed).
  const { videoPlayback, loading: videoLoading } = useVideoPlayback(lesson);

  if (!lesson) {
    return (
      <Card
        sx={{
          minHeight: 480,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 4,
          bgcolor: 'grey.50',
          border: '1px dashed',
          borderColor: 'divider',
          boxShadow: 0,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Lesson preview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click a lesson on the left to preview it here. Use the pencil
            icon on a row to open the full editor.
          </Typography>
        </Box>
      </Card>
    );
  }

  const meta = lessonTypeMeta(lesson.lessonType);
  const gated = lesson.isGated === true;

  return (
    <Card sx={{ p: 0, overflow: 'hidden' }}>
      {/* Gate notice — surfaces the lock state above the header so it's
          obvious in the preview, not just in the builder list. */}
      {gated && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 3,
            py: 1.25,
            bgcolor: '#fff7ed',
            color: '#92400e',
            borderBottom: '1px solid #fed7aa',
            fontSize: '0.85rem',
          }}
        >
          <LockIcon fontSize="small" />
          <Typography variant="caption" sx={{ color: 'inherit' }}>
            <strong>Gated lesson</strong> — learners must complete every previous lesson before reaching this one.
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: 0.75,
                bgcolor: `${meta.color}15`,
                color: meta.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {meta.icon}
            </Box>
            <Chip
              label={meta.label}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: `${meta.color}15`,
                color: meta.color,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {lesson.title || 'Untitled lesson'}
          </Typography>
          {lesson.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {lesson.description}
            </Typography>
          )}
        </Box>
        {onEdit && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => onEdit(lesson)}
            sx={{
              color: 'text.primary',
              borderColor: 'rgba(0,0,0,0.23)',
              flexShrink: 0,
              '&:hover': { borderColor: 'rgba(0,0,0,0.5)', bgcolor: 'action.hover' },
            }}
          >
            Edit lesson
          </Button>
        )}
      </Box>

      {/* Type-specific body. Best-effort previews — full fidelity stays
          in the dedicated lesson editor / the learner view. */}
      <Box sx={{ p: 3 }}>
        {lesson.lessonType === LessonType.Text && lesson.textContent ? (
          <Box
            sx={{
              maxHeight: 520,
              overflow: 'auto',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              p: 2.5,
              bgcolor: 'background.paper',
            }}
          >
            <TiptapViewer content={lesson.textContent} />
          </Box>
        ) : lesson.lessonType === LessonType.Video ? (
          // Real video preview — VideoViewer handles Mux (with the
          // signed playback token from useVideoPlayback), Wistia (via
          // its own embed), and the "still processing" / "not uploaded"
          // states. requireFullWatch=false because this is an admin
          // preview, not a learner completion path.
          <Box
            sx={{
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: '#0d1b2a',
            }}
          >
            <VideoViewer
              lesson={lesson}
              playback={videoPlayback}
              loading={videoLoading}
              lessonTitle={lesson.title || 'Lesson'}
              requireFullWatch={false}
              sx={{
                width: '100%',
                height: 'auto',
                aspectRatio: '16 / 9',
                maxHeight: 480,
                display: 'block',
              }}
            />
          </Box>
        ) : lesson.lessonType === LessonType.Quiz ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Quiz · pass threshold {lesson.quizSettings?.passThreshold ?? 80}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Open the lesson editor to view and edit questions.
            </Typography>
          </Box>
        ) : (lesson.lessonType === LessonType.PPT ||
            lesson.lessonType === LessonType.Word ||
            lesson.lessonType === LessonType.PDF) ? (
          <Box
            sx={{
              p: 3,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1,
                bgcolor: `${meta.color}15`,
                color: meta.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FileTextIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {lesson.attachedUrl || lesson.file || 'Document attached'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {meta.label} · open the lesson editor to replace or preview the file.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No preview available for this lesson type. Open the editor for details.
          </Typography>
        )}
      </Box>
    </Card>
  );
}
